import { BALANCE, FACTIONS } from '@sd/content';
import { t } from '@sd/i18n';
import type { GameEvent, GameState } from '@sd/sim';
import type { Viewer } from '../map/describe';

/**
 * Tutorial hints for the first steps (#59, GDD §13 MVP: «туториальные
 * подсказки»). One hint at a time, in order; each goes away by itself once
 * the player has done what it asks, or when the player dismisses it. A
 * crisis jumps the queue. Pure: state, events and progress in, the hint out.
 */
export const HINT_IDS = [
  'build',
  'research',
  'trade',
  'explore',
  'ships',
  'outpost',
  'race',
  'crisis',
] as const;
export type HintId = (typeof HINT_IDS)[number];

/** The side panel tab a hint points at. */
export type HintTab = 'planet' | 'market' | 'research' | 'fleet' | 'diplomacy';

export interface Hint {
  readonly id: HintId;
  readonly title: string;
  readonly text: string;
  /** «2 of 7»; the crisis hint is out of the sequence and has none. */
  readonly step: string;
  readonly tab: HintTab | null;
}

export interface TutorialProgress {
  /** Done by the player's own actions, seen in the events. */
  readonly did: ReadonlySet<HintId>;
  /** Closed with «Понятно». */
  readonly dismissed: ReadonlySet<HintId>;
}

export const emptyProgress = (): TutorialProgress => ({ did: new Set(), dismissed: new Set() });

const SEQUENCE: readonly HintId[] = HINT_IDS.filter((id) => id !== 'crisis');
const TAB: Readonly<Record<HintId, HintTab | null>> = {
  build: 'planet',
  research: 'research',
  trade: 'market',
  explore: null,
  ships: 'fleet',
  outpost: 'fleet',
  race: null,
  crisis: 'diplomacy',
};
/** The race hint waits until the player has had a few cycles to look around. */
const RACE_AFTER_CYCLES = 3;

/** What the player's own events show they have done. */
export function observe(
  progress: TutorialProgress,
  events: readonly GameEvent[],
  me: string,
): TutorialProgress {
  const did = new Set(progress.did);
  for (const e of events) {
    if (!('playerId' in e) || e.playerId !== me) continue;
    if (e.type === 'constructionStarted') did.add('build');
    else if (e.type === 'researchStarted') did.add('research');
    else if (e.type === 'traded') did.add('trade');
    else if (e.type === 'found') did.add('explore');
    else if (e.type === 'shipQueued') did.add('ships');
    else if (e.type === 'outpostFounded') did.add('outpost');
  }
  return did.size === progress.did.size ? progress : { ...progress, did };
}

/** Done, judging by the state itself: a match joined late counts what is already there. */
function doneInState(state: GameState, me: string, id: HintId): boolean {
  const player = state.players.find((p) => p.id === me);
  if (!player) return true;
  const fleets = state.fleets.filter((f) => f.owner === me);
  switch (id) {
    case 'build':
      return player.planet.buildings.some((b) => b.construction !== null || b.level > 1);
    case 'research':
      return player.research.active.length > 0 || player.techs.length > 0;
    case 'explore':
      return fleets.some((f) => f.scan !== null);
    case 'ships':
      return (
        player.shipyard.length > 0 || fleets.some((f) => f.ships.some((s) => s.cls === 'transport'))
      );
    case 'outpost':
      return state.outposts.some((o) => o.owner === me);
    case 'crisis': {
      const crisis = state.crises.find((c) => c.warned.includes(me));
      const project = crisis && state.projects.find((p) => p.crisisId === crisis.id);
      return !project || (project.byPlayer[me]?.points ?? 0) > 0 || state.tick >= project.deadline;
    }
    default:
      return false;
  }
}

export function currentHint(state: GameState, v: Viewer, progress: TutorialProgress): Hint | null {
  if (state.result) return null;
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const open = (id: HintId) =>
    !progress.did.has(id) && !progress.dismissed.has(id) && !doneInState(state, me, id);
  const hint = (id: HintId, step: string): Hint => ({
    id,
    title: t(locale, `hint.${id}.title`),
    text: t(locale, `hint.${id}.text`, {
      good: t(locale, `resource.${FACTIONS[player.faction].cheapGood}`).toLowerCase(),
    }),
    step,
    tab: TAB[id],
  });

  // A crisis with a project to give to comes first.
  if (state.crises.some((c) => c.warned.includes(me)) && open('crisis')) return hint('crisis', '');

  // No fixed path to victory: a hint waits until it makes sense for the
  // start the player chose. Research needs science, so its hint waits for a
  // research center (or science from a find); the race waits a few cycles.
  const cycle = Math.floor(state.tick / BALANCE.cycleTicks);
  const hasScience =
    player.planet.science > 0 ||
    player.planet.buildings.some((b) => b.id === 'researchCenter' && b.construction === null);
  const ready = (id: HintId) =>
    id === 'race' ? cycle >= RACE_AFTER_CYCLES : id === 'research' ? hasScience : true;
  const done = SEQUENCE.filter((id) => !open(id)).length;
  const next = SEQUENCE.find((id) => open(id) && ready(id));
  return next ? hint(next, t(locale, 'hint.step', { done, total: SEQUENCE.length })) : null;
}
